/** Entra App Registration roles (must match WebOpsApi manifest values). */
export type OpsRole =
  | 'ItSysAdmin'
  | 'OpsOperations'
  | 'OpsPharmacy'
  | 'OpsHRCompany'
  | 'OpsHRContract'
  | 'OpsFinance'
  | 'OpsSales';

const OPS_DISPLAY_ORDER: OpsRole[] = [
  'ItSysAdmin',
  'OpsOperations',
  'OpsPharmacy',
  'OpsHRCompany',
  'OpsHRContract',
  'OpsFinance',
  'OpsSales'
];

export const OPS_ROLES: readonly OpsRole[] = OPS_DISPLAY_ORDER;

export function getHighestOpsRole(roles: string[]): OpsRole | null {
  for (const role of OPS_DISPLAY_ORDER) {
    if (roles.includes(role)) return role;
  }
  return null;
}

/** ItSysAdmin or either HR segment role — gates Human Relations / Time Off routes. */
export function hasAnyHrRole(roles: string[]): boolean {
  return (
    roles.includes('ItSysAdmin') ||
    roles.includes('OpsHRCompany') ||
    roles.includes('OpsHRContract')
  );
}

export function hasOpsRole(roles: string[], required: OpsRole): boolean {
  return roles.includes('ItSysAdmin') || roles.includes(required);
}

export function isItSysAdmin(roles: string[]): boolean {
  return roles.includes('ItSysAdmin');
}

/** Settings nav + route: ItSysAdmin or either HR segment — not Pharmacy/Sales alone. */
export function hasSettingsPageAccess(roles: string[]): boolean {
  return (
    roles.includes('ItSysAdmin') ||
    roles.includes('OpsHRCompany') ||
    roles.includes('OpsHRContract')
  );
}

/** Secure Pharm Reports — Pharmacy, Finance, Sales, and admin roles (see AZURE_SETUP.md). */
export function hasSecurePharmReportsAccess(roles: string[]): boolean {
  return (
    roles.includes('ItSysAdmin') ||
    roles.includes('OpsPharmacy') ||
    roles.includes('OpsFinance') ||
    roles.includes('OpsSales')
  );
}

/** Storage dashboard cards — Pharmacy and admin only (not Finance/Sales/HR). */
export function hasSecurePharmDashboardAccess(roles: string[]): boolean {
  return roles.includes('ItSysAdmin') || roles.includes('OpsPharmacy');
}

/**
 * Sales / Pharmacy Operations menus while withheld from OpsSales / OpsPharmacy.
 * ItSysAdmin and OpsOperations (ops admin) can still open those tools.
 */
export function hasUnreleasedDepartmentNavAccess(roles: string[]): boolean {
  return roles.includes('ItSysAdmin') || roles.includes('OpsOperations');
}

/** Pharmacy Operations pages and patient-tracking dashboard cards. */
export function hasPharmacyOpsAccess(roles: string[]): boolean {
  return (
    roles.includes('ItSysAdmin') ||
    roles.includes('OpsOperations') ||
    roles.includes('OpsPharmacy')
  );
}

/** Sales CE tools routes — department role still deep-links; nav is separate. */
export function hasSalesToolsAccess(roles: string[]): boolean {
  return (
    roles.includes('ItSysAdmin') ||
    roles.includes('OpsOperations') ||
    roles.includes('OpsSales')
  );
}

/** Patient-tracking dashboard cards — same gate as pharmacy ops. */
export function hasPharmacyTrackingDashboardAccess(roles: string[]): boolean {
  return hasPharmacyOpsAccess(roles);
}
